import React, { useEffect, useState } from "react";
import { ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollLock } from "@/hooks/useScrollLock";
import { Loading } from "@/components/fortress";
// import { Button } from "@castlery/fortress";
import { cn } from "@/lib/utils";
import { useChatStore } from "@/stores/chatStore";
import {
  TAILWIND_MD_BREAKPOINT,
  TAILWIND_SM_BREAKPOINT,
} from "@/lib/tailwindScreens";

interface ChatFrameworkProps {
  className?: string;
  contentRef?: React.RefObject<HTMLDivElement>;
  header?: React.ReactNode;
  headerContent?: React.ReactNode;
  headerBorder?: boolean;
  headerShadow?: boolean;
  footer?: React.ReactNode;
  footerContent?: React.ReactNode;
  isLoading?: boolean;
  children: React.ReactNode;
  onClose: () => void;
  onBack?: () => void;
  // 新增：是否启用滚动锁定
  enableScrollLock?: boolean;
}

export const ChatFramework = React.forwardRef<
  HTMLDivElement,
  ChatFrameworkProps
>(
  (
    {
      className = "",
      contentRef,
      header,
      headerContent,
      headerBorder,
      headerShadow,
      footer,
      footerContent,
      isLoading = false,
      children,
      onClose,
      onBack,
    },
    ref
  ) => {
    const { isMobile } = useChatStore();
    const [isTablet, setIsTablet] = useState(false);

    useEffect(() => {
      const checkScreenSizeHandler = () => {
        const width = window.innerWidth;
        setIsTablet(
          width >= TAILWIND_SM_BREAKPOINT && width < TAILWIND_MD_BREAKPOINT,
        );
      };

      checkScreenSizeHandler();
      window.addEventListener("resize", checkScreenSizeHandler);

      return () => {
        window.removeEventListener("resize", checkScreenSizeHandler);
      };
    }, []);

    // 根据屏幕宽度决定是否启用滚动锁定
    // 只有移动端和平板启用，桌面端不启用
    const shouldEnableScrollLock =
      isMobile || // 移动端启用
      isTablet; // 平板启用

    // 使用自定义滚动锁定 Hook
    useScrollLock(shouldEnableScrollLock);

    const frameworkContent = (
      <div
        ref={ref}
        id="psa-chat-framework"
        className={cn(
          "flex flex-col relative bg-white overflow-hidden font-aime",
          "w-full h-full",
          className
        )}
      >
        {/* 头部 */}
        <div
          className={cn(
            "flex justify-between items-center",
            "px-[12px] py-[4px]",
            // 移动端：增加头部高度，提供更好的触摸体验
            "min-h-[60px]",
            // 桌面端：保持原有高度
            "sm:min-h-auto",
            {
              "border-b": headerBorder,
              "shadow-sm": headerShadow,
            }
          )}
        >
          {onBack && (
            <div className="w-[80px] flex justify-start">
              <Button
                className={cn(
                  "text-[13px] text-foreground ",
                  // 移动端：增加头部高度，提供更好的触摸体验
                  "min-h-[44px]",
                  // 桌面端：保持原有高度
                  "sm:min-h-auto"
                )}
                variant="link"
                size="sm"
                onClick={onBack}
              >
                <ChevronLeft className="pr-1 w-4 h-4" />
                Back
              </Button>
            </div>
          )}

          {header && (
            <div className="flex items-center">
              <h2 className="text-gray-900 font-bold w-fit h-fit">{header}</h2>
            </div>
          )}

          <div className="w-[80px] flex justify-end">
            <Button
              className={cn(
                "text-[13px] text-foreground ",
                // 移动端：增加头部高度，提供更好的触摸体验
                "min-h-[44px]",
                // 桌面端：保持原有高度
                "sm:min-h-auto"
              )}
              variant="link"
              size="sm"
              onClick={onClose}
            >
              <svg
                width="12"
                height="13"
                viewBox="0 0 12 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.74805 2.34961C9.85487 2.34963 9.95697 2.39305 10.0322 2.46875C10.1075 2.54452 10.1504 2.6472 10.1504 2.75391C10.1503 2.86051 10.1074 2.96339 10.0322 3.03906L6.59277 6.50098L10.0303 9.96094C10.0865 10.0174 10.1242 10.0899 10.1396 10.168C10.1551 10.246 10.1474 10.3268 10.1172 10.4004C10.0869 10.474 10.0358 10.5376 9.96973 10.582C9.90369 10.6264 9.82569 10.6503 9.74609 10.6504C9.64325 10.6504 9.53941 10.6102 9.46094 10.5312L5.99805 7.04492L2.53613 10.5312C2.46087 10.607 2.3588 10.6504 2.25195 10.6504C2.14513 10.6504 2.04303 10.6069 1.96777 10.5312C1.89253 10.4555 1.84959 10.3528 1.84961 10.2461C1.84969 10.1395 1.89257 10.0366 1.96777 9.96094L5.4043 6.50098L1.96484 3.03906L1.96191 3.03613C1.89015 2.95971 1.85109 2.85768 1.85254 2.75293C1.85404 2.64824 1.8959 2.54798 1.96973 2.47363C2.04362 2.39923 2.14409 2.35698 2.24902 2.35547C2.32774 2.35441 2.4043 2.37629 2.46973 2.41797L2.53125 2.46582L2.53418 2.46875L5.99902 5.95605L9.46387 2.46875C9.53913 2.39301 9.6412 2.34963 9.74805 2.34961Z"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth="0.3"
                />
              </svg>
            </Button>
          </div>
        </div>

        {/* 头部内容区域 */}
        <div className="flex justify-between px-[24px]">
          <div className="flex-1">{headerContent}</div>
        </div>

        {/* 内容区域 - 添加滚动优化 */}
        <div
          ref={contentRef}
          className="flex-1 p-4 overflow-y-auto relative overscroll-contain contain-strict"
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loading theme="dark" />
            </div>
          ) : (
            <>
              {/* 内容区域 */}
              {children}
            </>
          )}
        </div>

        <div className="relative">
          <div className="pointer-events-none absolute inset-x-0 -top-5 h-5 bg-gradient-to-b from-transparent via-white/40 to-white/80" />
          {/* 底部内容区域 */}
          <div className="flex justify-between">
            <div className="flex-1">{footerContent}</div>
          </div>

          {/* 底部 */}
          <div
            className={cn(
              "flex justify-between",
              // 移动端：增加底部安全区域
              "pb-safe sm:pb-0",
            )}
          >
            <div className="flex-1">{footer}</div>
          </div>
        </div>
      </div>
    );

    return frameworkContent;
  }
);
